/**
 * Chargement de reCAPTCHA v3 et obtention d'un jeton.
 *
 * Le script `api.js?render=<clé>` est injecté à la demande avec la clé configurée
 * (REACT_APP_RECAPTCHA_SITE_KEY), puis `grecaptcha.execute` fournit un jeton à usage
 * unique, valable deux minutes, au moment de l'envoi.
 */

const SCRIPT_ID = 'recaptcha-v3-script';
const LOAD_TIMEOUT_MS = 15000;
const EXECUTE_TIMEOUT_MS = 15000;

let loading = null;

function isReady(grecaptcha) {
    return Boolean(grecaptcha && typeof grecaptcha.ready === 'function' && typeof grecaptcha.execute === 'function');
}

/**
 * Charge le script reCAPTCHA v3 une seule fois. En cas d'échec, l'appel suivant
 * retente le chargement.
 */
export function loadRecaptcha(siteKey, options = {}) {
    if (isReady(window.grecaptcha)) {
        return Promise.resolve(window.grecaptcha);
    }
    if (loading) {
        return loading;
    }
    const timeoutMs = options.timeoutMs || LOAD_TIMEOUT_MS;
    loading = new Promise((resolve, reject) => {
        const previous = document.getElementById(SCRIPT_ID);
        if (previous && previous.parentNode) {
            previous.parentNode.removeChild(previous);
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(siteKey) + '&hl=fr';
        script.async = true;
        script.defer = true;

        let settled = false;
        const finish = (error) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            loading = null;
            if (error) {
                reject(error);
            } else {
                resolve(window.grecaptcha);
            }
        };
        const timer = setTimeout(() => finish(new Error('reCAPTCHA: délai de chargement dépassé')), timeoutMs);
        script.onload = () => finish(isReady(window.grecaptcha) ? null : new Error('reCAPTCHA: script incomplet'));
        script.onerror = () => finish(new Error('reCAPTCHA: script non chargé'));
        document.head.appendChild(script);
    });
    return loading;
}

/**
 * Retourne un jeton reCAPTCHA v3 pour `action`. Rejette si le script ne se charge
 * pas, si `execute` échoue ou ne répond pas, ou si le jeton est vide.
 */
export async function executeRecaptcha(siteKey, action, options = {}) {
    const grecaptcha = await loadRecaptcha(siteKey, options);
    const timeoutMs = options.executeTimeoutMs || EXECUTE_TIMEOUT_MS;
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('reCAPTCHA: pas de réponse')), timeoutMs);
        const done = (error, token) => {
            clearTimeout(timer);
            if (error) {
                reject(error);
            } else if (!token) {
                reject(new Error('reCAPTCHA: jeton vide'));
            } else {
                resolve(token);
            }
        };
        try {
            grecaptcha.ready(() => {
                try {
                    Promise.resolve(grecaptcha.execute(siteKey, {action}))
                        .then(token => done(null, token), error => done(error || new Error('reCAPTCHA: échec')));
                } catch (error) {
                    done(error);
                }
            });
        } catch (error) {
            done(error);
        }
    });
}
